// encryption.ts

const SALT = 'CoreVictoryJournal';
const ITERATIONS = 100000;

const ENCRYPTION_VERSIONS = {
  V1: 1,
  V2: 2
} as const;

/**
 * Derives a CryptoKey using PBKDF2 based on user ID and version
 */
const deriveKey = async (userId: string, version: number): Promise<CryptoKey> => {
  if (!userId) {
    throw new Error('User ID is required for key derivation');
  }

  const encoder = new TextEncoder();
  const keyMaterial = await crypto.subtle.importKey(
    'raw',
    encoder.encode(version === ENCRYPTION_VERSIONS.V1 ? userId : userId + SALT),
    { name: 'PBKDF2' },
    false,
    ['deriveKey']
  );

  return crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt: encoder.encode(version === ENCRYPTION_VERSIONS.V1 ? SALT : SALT + version),
      iterations: ITERATIONS,
      hash: 'SHA-256'
    },
    keyMaterial,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  );
};

/**
 * Encrypts a string using AES-GCM and encodes as base64
 */
export const encryptText = async (text: string, userId: string): Promise<string> => {
  if (text == null || !userId) {
    throw new Error('Text and user ID are required for encryption');
  }

  try {
    const key = await deriveKey(userId, ENCRYPTION_VERSIONS.V2);
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const encoded = new TextEncoder().encode(text);

    const encryptedData = await crypto.subtle.encrypt(
      { name: 'AES-GCM', iv },
      key,
      encoded
    );

    const encryptedBytes = new Uint8Array(encryptedData);
    const output = new Uint8Array(1 + iv.length + encryptedBytes.length);
    output[0] = ENCRYPTION_VERSIONS.V2; // Version prefix
    output.set(iv, 1);
    output.set(encryptedBytes, 1 + iv.length);

    return btoa(String.fromCharCode(...output));
  } catch (error) {
    console.error('Encryption error:', error);
    throw new Error('Encryption failed');
  }
};

/**
 * Decrypts base64 string to plain text using AES-GCM
 */
export const decryptText = async (cipherText: string, userId: string): Promise<string> => {
  if (cipherText == null || !userId) {
    throw new Error('Cipher text and user ID are required for decryption');
  }

  try {
    // First try V1 decryption (no version byte)
    try {
      const combined = new Uint8Array(
        atob(cipherText).split('').map(char => char.charCodeAt(0))
      );

      const iv = combined.slice(0, 12);
      const encryptedData = combined.slice(12);

      const key = await deriveKey(userId, ENCRYPTION_VERSIONS.V1);
      const decryptedData = await crypto.subtle.decrypt(
        {
          name: 'AES-GCM',
          iv: iv
        },
        key,
        encryptedData
      );

      return new TextDecoder().decode(decryptedData);
    } catch (v1Error) {
      // If V1 fails, try V2 decryption (with version byte)
      const combined = new Uint8Array(
        atob(cipherText).split('').map(char => char.charCodeAt(0))
      );

      const version = combined[0];
      const iv = combined.slice(1, 13);
      const encryptedData = combined.slice(13);

      const key = await deriveKey(userId, version);
      const decryptedData = await crypto.subtle.decrypt(
        {
          name: 'AES-GCM',
          iv: iv
        },
        key,
        encryptedData
      );

      return new TextDecoder().decode(decryptedData);
    }
  } catch (err) {
    console.error('Decryption error:', err);
    throw new Error('Decryption failed');
  }
};